import { Component, inject } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { EMPTY } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { CstDialogResult } from "src/app/common-components/dialogs/cst-dialog-enums";
import { CstDialogsService } from "src/app/common-components/dialogs/cst-dialogs.service";
import { FormControlBase } from "src/app/common-components/form-controls/form-control-base";
import { CstResponsivePopupComponentBase } from "src/app/common-components/resp-popup";
import { CstResponsivePopupService } from "src/app/common-components/resp-popup/services/resp-popup.service";
import {
  AnlageDetailDto,
  AnlageDetailIncludes,
  AnlagenClientProvider,
  FacilityNoType,
  FacilityPatchCommand,
  IFacilityLocationUpsertCommand,
  LKZLookupDto,
  StandortDetailDto,
  ValidationResult,
} from "src/app/web-api/web-api";
import { AnlageApiFacilityLocationSelectDialogComponent } from "../facility-location-select/facility-location-select.component";
import { AlertService } from "src/app/services";
import { compare } from "fast-json-patch";
import {
  CstFormBuilder,
  CstFormGroup,
} from "src/app/_uncategorised_models_services/cst-form-group";

export interface IAnlageApiFaciliyUpdateDialogData {
  id: number;
}

interface IFacilityUpdateCommand {
  id: number;
  locationID: number;
  location?: IFacilityLocationUpsertCommand;
  label: string;
  buildingTypeID: number;
  contactEmail: string;
  appointmentRequired: boolean;
}

@Component({
  templateUrl: "./facility-update.component.html",
  standalone: false,
})
export class AnlageApiFaciliyUpdateDialogComponent extends CstResponsivePopupComponentBase<
  IAnlageApiFaciliyUpdateDialogData,
  AnlageDetailDto
> {
  private _client = inject(AnlagenClientProvider);
  private _popupService = inject(CstResponsivePopupService);
  private _dlgs = inject(CstDialogsService);
  private _alrt = inject(AlertService);

  private _formValueChangesRecursionBreaker = false;
  private _originalValues: IFacilityUpdateCommand = null;
  public FacilityNoType = FacilityNoType;
  public form: CstFormGroup<IFacilityUpdateCommand> = null;
  public loading = false;

  public constructor() {
    const fb = inject(CstFormBuilder);

    super();
    this.form = fb.group<IFacilityUpdateCommand>({
      id: new FormControl(null, Validators.required),
      locationID: new FormControl(null),
      location: fb.group<IFacilityLocationUpsertCommand>({
        id: new FormControl(null),
        name: new FormControl(null, Validators.maxLength(200)),
        street: new FormControl(null, Validators.maxLength(126)),
        streetNo: new FormControl(null, Validators.maxLength(10)),
        city: new FormControl(null, Validators.maxLength(126)),
        postalCode: new FormControl(null, Validators.maxLength(10)),
        country: new FormControl("Deutschland", Validators.maxLength(126)),
        countryID: new FormControl(52),
      }),
      label: new FormControl(
        null,
        Validators.compose([Validators.required, Validators.maxLength(63)]),
      ),
      buildingTypeID: new FormControl(null),
      contactEmail: new FormControl(
        null,
        Validators.compose([Validators.email, Validators.maxLength(200)]),
      ),
      appointmentRequired: new FormControl(false),
    });

    this.form.valueChanges.subscribe((value) => {
      if (this._formValueChangesRecursionBreaker) {
        return;
      }
      this._formValueChangesRecursionBreaker = true;
      if (value.locationID && !value.location.id) {
        this.grpLocation.disable();
      } else {
        this.grpLocation.enable();
      }
      this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
      this._formValueChangesRecursionBreaker = false;
    });
  }

  public get grpLocation() {
    return this.form.controls.location as FormGroup;
  }

  public onInitialised(): void {
    if (!this.data.id) {
      console.error("required 'id' property is missing in data!");
      this.close(null);
      return;
    }
    this.loading = true;
    this._client
      .getSingle(this.data.id, null, AnlageDetailIncludes.Location)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(
        (result) => {
          this._originalValues = {
            id: result.anlageID,
            locationID: result.standortID,
            location: result.location
              ? {
                  id: result.location.id,
                  name: result.location.name,
                  street: result.location.street,
                  streetNo: result.location.streetNo,
                  city: result.location.city,
                  postalCode: result.location.postalCode,
                  country: result.location.country,
                  countryID: result.location.countryID,
                }
              : null,
            label: result.detail,
            buildingTypeID: result.buildingTypeID,
            contactEmail: result.contactMail,
            appointmentRequired: result.appointmentRequired,
          };
          this.form.patchValue(this._originalValues);
        },
        (error) => {
          this._alrt.showSnackbarError(error).subscribe();
          this.close(null);
        },
      );
  }

  public canAccept() {
    const result = !this.loading && this.form.valid;
    return result;
  }

  public onAccept() {
    if (!this.canAccept()) {
      return;
    }
    const val = this.form.value;
    const patch = compare(this._originalValues, val);
    if (patch.length == 0) {
      this.onCancel();
      return;
    }
    const cmd = new FacilityPatchCommand();
    cmd.init({ id: val.id, patch: { operations: patch } });
    this.loading = true;
    this._client
      .anlagePatch(cmd)
      .pipe(
        catchError((error) => {
          if (error instanceof ValidationResult) {
            setTimeout(() => {
              FormControlBase.ApplyValidationResultToForm(this.form, error);
              FormControlBase.ApplyValidationResultToForm(
                this.grpLocation,
                error,
              );
            }, 1);
            return EMPTY;
          }
          console.log(error);
          return EMPTY;
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe((result) => {
        this.close(result);
      });
  }

  public onCancel() {
    this.close();
  }

  public onFacilityLocationSelect() {
    const ref = this._popupService.open<
      AnlageApiFacilityLocationSelectDialogComponent,
      any,
      StandortDetailDto
    >(AnlageApiFacilityLocationSelectDialogComponent);
    ref.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this._dlgs
        .AskYesCancel(
          // eslint-disable-next-line max-len
          $localize`:@@AnlageAPI_FacilityCreate_Prompt_ChooseLocation_Text:Use this location? \n\n ${result?.street ?? ""} ${result?.streetNo ?? ""}, ${result?.postalCode ?? ""} ${result?.city ?? ""}`,
          undefined,
          $localize`:@@AnlageAPI_FacilityCreate_Prompt_ChooseLocation_ButtonCopy:Accept`,
        )
        .subscribe((dlgResult) => {
          if (dlgResult === CstDialogResult.Cancel) {
            return;
          } else if (dlgResult === CstDialogResult.Yes) {
            this.form.patchValue({ locationID: null, location: result });
          }
        });
    });
  }

  public onCountrySelected(item: LKZLookupDto) {
    this.form.patchValue({
      location: {
        country: item.land,
      },
    });
  }
}
